/**
 * Delete account screen — D.L. 144/2024 right-to-erasure.
 *
 * User requests account deletion with an optional reason. Entering the
 * request starts a 30-day grace period (hard-delete performed by a daily
 * cron). During the grace window, the user can cancel the request from
 * this same screen.
 */
import { useState, useCallback, useEffect } from 'react'
import { View, Text, Alert, Platform } from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSheet } from '@/components/ui/GlassSheet'
import { useTranslation } from 'react-i18next'
import { impact, notification, ImpactStyle, NotificationType } from '@/lib/utils/haptics'
import { formatBookingDate } from '@/lib/utils/dates'
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { AlertTriangle, Clock, Trash2 } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { useSemanticColors, SPACING, RADIUS, DURATION } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useAuth } from '@/lib/hooks/use-auth'
import { complianceService, type DeletionStatus } from '@/lib/services/compliance.service'
import { captureError } from '@/lib/utils/observability'
import { toast } from '@/lib/toast'

export default function DeleteAccountScreen() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const { user, signOut } = useAuth()

  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState<DeletionStatus | null>(null)
  const [statusError, setStatusError] = useState(false)

  // Load current deletion state.
  //
  // `isPending` is derived purely from `status`, so a swallowed read collapsed
  // to "nothing pending" — a user already scheduled for deletion was shown the
  // request form again, with no hint that we simply could not tell. STANDARDS
  // §4 forbids the silent catch: track the failure and say so.
  const loadStatus = useCallback(async () => {
    if (!user?.id) return
    try {
      const s = await complianceService.getDeletionStatus(user.id)
      setStatus(s)
      setStatusError(false)
    } catch (err) {
      setStatusError(true)
      captureError(err, { screen: 'profile/delete-account', action: 'read-deletion-status' })
    }
  }, [user?.id])

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])

  const executeDelete = useCallback(async () => {
    if (Platform.OS !== 'web') {
      impact(ImpactStyle.Heavy)
    }
    setSubmitting(true)
    try {
      await complianceService.requestDeletion(reason.trim() || undefined)
      toast.success(t('compliance.delete_success'))
      // Deleting has to look like deleting. The RPC only stamps
      // delete_requested_at and schedules the purge 30 days out, so the account
      // used to stay fully usable and signed in — the person tapped "eliminar
      // cuenta", read a success toast, and nothing changed. App Store guideline
      // 5.1.1(v) says the account may not be left merely "temporarily
      // deactivated"; this one was not even that.
      //
      // Records are never deleted here (the retention rule owns that, and the
      // nightly purge honours it), so what ends immediately is ACCESS: the
      // session closes. create-payment refuses the account as well, server-side,
      // so nothing can be booked in the meantime.
      //
      // Signing back in still works and still reaches "cancelar eliminación" —
      // the grace period exists for people who change their mind, and a door
      // that cannot be reopened is not a grace period.
      await signOut()
    } catch (err) {
      if (__DEV__) console.error('[Delete account]', err)
      if (Platform.OS !== 'web') {
        void notification(NotificationType.Error)
      }
      toast.error(t('compliance.delete_failed'))
    } finally {
      setSubmitting(false)
    }
  }, [reason, t, signOut])

  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const handleRequest = useCallback(() => {
    if (Platform.OS === 'web') {
      // Web: use state-driven modal instead of window.confirm
      setShowConfirmModal(true)
      return
    }

    Alert.alert(t('compliance.delete_confirm_title'), t('compliance.delete_confirm_desc'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('compliance.delete_confirm_yes'),
        style: 'destructive',
        onPress: () => void executeDelete(),
      },
    ])
  }, [t, executeDelete])

  const handleCancel = useCallback(async () => {
    setSubmitting(true)
    try {
      await complianceService.cancelDeletion()
      await loadStatus()
      toast.success(t('compliance.cancel_success'))
      if (Platform.OS !== 'web') {
        void notification(NotificationType.Success)
      }
    } catch (err) {
      if (__DEV__) console.error('[Cancel delete]', err)
      toast.error(t('compliance.cancel_failed'))
    } finally {
      setSubmitting(false)
    }
  }, [t, loadStatus])

  const isPending = !!status?.deleteRequestedAt
  const scheduledDate = status?.deleteScheduledAt
    ? formatBookingDate(status.deleteScheduledAt, 'es', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('compliance.delete_title')} />

      {/* `flex: 1` here + `flexGrow: 1` on the content is what lets the
          spacer below the form absorb the leftover height, so the destructive
          CTA closes the screen instead of floating mid-display with the bottom
          third of the phone empty under it. The spacer collapses to zero as
          soon as the content is taller than the viewport, so it never adds a
          scroll. */}
      <KeyboardAwareView
        includeTopInset={false}
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: SPACING.lg,
          paddingBottom: SPACING.xl * 2,
          gap: SPACING.lg,
        }}
      >
        {isPending ? (
          <>
            <Animated.View
              entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
              collapsable={false}
              style={{
                flexDirection: 'row',
                gap: SPACING.md,
                backgroundColor: `${colors.warning}14`,
                padding: SPACING.md,
                borderRadius: RADIUS.lg,
              }}
            >
              <Clock size={22} color={colors.warning} />
              <View style={{ flex: 1, gap: SPACING.xs }}>
                <Text
                  style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}
                >
                  {t('compliance.delete_pending_title')}
                </Text>
                <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary }}>
                  {t('compliance.delete_pending_desc', { date: scheduledDate })}
                </Text>
              </View>
            </Animated.View>
            {/* Negative bottom margin cancels the column gap the spacer would
                otherwise add, so card → CTA stays exactly SPACING.lg once the
                spacer collapses. */}
            <View style={{ flex: 1, marginBottom: -SPACING.lg }} />
            <Button
              title={t('compliance.delete_cancel')}
              onPress={handleCancel}
              variant="primary"
              size="lg"
              disabled={submitting}
              loading={submitting}
              testID="compliance.delete-cancel"
            />
          </>
        ) : (
          <>
            {statusError ? (
              <View
                testID="compliance.delete-status-error"
                style={{
                  gap: SPACING.sm,
                  backgroundColor: `${colors.warning}14`,
                  padding: SPACING.md,
                  borderRadius: RADIUS.lg,
                }}
              >
                <Text
                  style={{
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                    lineHeight: 18,
                  }}
                >
                  {t('compliance.delete_status_error')}
                </Text>
                <Button
                  title={t('common.retry')}
                  onPress={() => void loadStatus()}
                  variant="secondary"
                  testID="compliance.delete-status-retry"
                />
              </View>
            ) : null}

            <Animated.View
              entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
              collapsable={false}
              style={{
                flexDirection: 'row',
                gap: SPACING.md,
                backgroundColor: `${colors.error}10`,
                padding: SPACING.md,
                borderRadius: RADIUS.lg,
              }}
            >
              <AlertTriangle size={22} color={colors.textError} />
              <View style={{ flex: 1, gap: SPACING.xs }}>
                <Text style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textError }}>
                  {t('compliance.delete_warning_title')}
                </Text>
                <Text
                  style={{
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                    lineHeight: 18,
                  }}
                >
                  {t('compliance.delete_warning_desc')}
                </Text>
              </View>
            </Animated.View>

            {/* TextInput already carries FORM_FIELD_GAP (32) below itself, so
                the column's own SPACING.lg on top of it opened a 56pt void
                between the field and the CTA — twice any other gap on the
                screen. Cancel the column gap here so field → CTA lands on
                SPACING.xl. */}
            <View style={{ marginBottom: -SPACING.lg }}>
              <TextInput
                label={t('compliance.delete_reason_label')}
                value={reason}
                onChangeText={setReason}
                placeholder={t('compliance.delete_reason_placeholder')}
                multiline
                numberOfLines={3}
                testID="compliance.delete-reason"
              />
            </View>

            {/* Absorbs whatever the form does not use so the CTA sits at the
                foot of the screen — same anchoring Editar Perfil gives its
                primary button. `marginBottom` cancels the column gap the
                spacer adds, keeping field → CTA at the 32pt the TextInput
                already reserves once the spacer collapses. */}
            <View style={{ flex: 1, marginBottom: -SPACING.lg }} />

            {/* The irreversible action must not be the same object as "Guardar"
                on Editar Perfil: the trash glyph is what tells them apart at a
                glance, before the confirm step. */}
            <Button
              title={t('compliance.delete_request')}
              onPress={handleRequest}
              variant="primary"
              size="lg"
              disabled={submitting}
              loading={submitting}
              icon={<Trash2 size={18} color="#FFFFFF" strokeWidth={2} />}
              testID="compliance.delete-request"
            />
          </>
        )}
      </KeyboardAwareView>

      {/* Web confirmation modal (replaces window.confirm) */}
      {Platform.OS === 'web' && (
        <AppModal
          visible={showConfirmModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowConfirmModal(false)}
        >
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING.lg,
            }}
          >
            <GlassSheet
              accessibilityViewIsModal
              accessibilityLabel={t('compliance.delete_confirm_title')}
              style={{
                borderRadius: RADIUS.xl,
                maxWidth: 400,
                width: '100%',
              }}
              contentStyle={{
                padding: SPACING.lg,
                gap: SPACING.md,
              }}
            >
              <Text
                style={{
                  fontSize: 18,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  textAlign: 'center',
                }}
              >
                {t('compliance.delete_confirm_title')}
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  textAlign: 'center',
                  lineHeight: 20,
                }}
              >
                {t('compliance.delete_confirm_desc')}
              </Text>
              <View style={{ flexDirection: 'row', gap: SPACING.sm, marginTop: SPACING.sm }}>
                <View style={{ flex: 1 }}>
                  <Button
                    title={t('common.cancel')}
                    variant="secondary"
                    size="lg"
                    onPress={() => setShowConfirmModal(false)}
                    testID="compliance.delete-modal-cancel"
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Button
                    title={t('compliance.delete_confirm_yes')}
                    variant="primary"
                    size="lg"
                    onPress={() => {
                      setShowConfirmModal(false)
                      void executeDelete()
                    }}
                    testID="compliance.delete-modal-confirm"
                  />
                </View>
              </View>
            </GlassSheet>
          </View>
        </AppModal>
      )}
    </ScreenContainer>
  )
}
